import React, { useState } from 'react';
import { router, Head, useForm, usePage } from '@inertiajs/react';
import { MicrovixIcon } from '@/frontend/components/DigitalWorkers/providerIcons';
import { Button } from '@/components/ui/button';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RefreshCw, Plug, AlertTriangle, Loader2 } from 'lucide-react';
import AssignWorkersModal from '@/frontend/components/Integrations/AssignWorkersModal';
import { ConnectionStatusChip } from '@/frontend/components/Integrations/ConnectionStatusChip';
import PostConnectionModal from '@/frontend/components/PostConnectionModal';
import { usePermissions } from '@/frontend/hooks/usePermissions';
import { useJustConnectedWorker } from '@/frontend/hooks/useJustConnectedWorker';
import DashboardLayout from '@/frontend/layouts/DashboardLayout';
import AssignedWorkersBanner from '@/frontend/components/Integrations/AssignedWorkersBanner';
import { BackLink } from '@/frontend/components/BackLink';
import type { MicrovixIntegration, AssignWorker } from './types';

interface Props {
  page_title: string;
  microvix_connected: boolean;
  microvix_integration?: MicrovixIntegration;
  digital_workers: AssignWorker[];
}

const MicrovixPage = ({
  page_title,
  microvix_connected,
  microvix_integration,
  digital_workers = [],
}: Props) => {
  const { can_manage_resources } = usePermissions();
  const { flash } = usePage<{ flash: { assign_workers?: boolean } }>().props;
  const [assignWorkersOpen, setAssignWorkersOpen] = useState(
    () => !!flash?.assign_workers && digital_workers.length > 0
  );
  const [savingWorkerAssignment, setSavingWorkerAssignment] = useState(false);
  const [showForm, setShowForm] = useState(!microvix_connected);
  const justConnected = useJustConnectedWorker(digital_workers);

  const form = useForm({
    api_key: '',
    portal_id: '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    form.post('/dashboard/integrations/microvix/connect', {
      preserveScroll: true,
      onSuccess: () => {
        setShowForm(false);
        form.reset();
      },
    });
  };

  return (
    <>
      <Head title={page_title} />
      <div className="px-4 py-6">
        <div className="space-y-6 min-h-full">
          <BackLink label="Integrações" fallbackHref="/dashboard/integrations" />

          {/* Header único (padrão da ficha de credencial): quadrado do ícone +
              nome + chip de status + ações. O "brilho" gradiente e o título
              central duplicado saíram. */}
          <div className="flex items-center gap-3">
            <span className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-secondary [&_svg]:size-6">
              <MicrovixIcon />
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2.5">
                <h1 className="text-[19px] font-semibold tracking-[-0.01em] text-foreground">Linx Microvix</h1>
                <ConnectionStatusChip connected={microvix_connected} />
              </div>
            </div>
            {microvix_connected && can_manage_resources && (
              <div className="flex shrink-0 items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setShowForm(true)}>
                  <RefreshCw className="h-4 w-4" />
                  <span className="hidden sm:inline">Reconectar</span>
                  <span className="sm:hidden">Recon.</span>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-muted-foreground hover:border-destructive/40 hover:text-destructive"
                  onClick={() => {
                    if (confirm('Tem certeza que deseja desconectar o Linx Microvix? Isso removerá a integração.')) {
                      router.delete('/dashboard/integrations/microvix/disconnect', { preserveScroll: true });
                    }
                  }}
                >
                  <span className="hidden sm:inline">Desconectar</span>
                  <span className="sm:hidden">Desc.</span>
                </Button>
              </div>
            )}
          </div>

          {!can_manage_resources && (
            <Alert variant="warning">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Você não tem permissão para gerenciar integrações.</AlertTitle>
              <AlertDescription>Você pode visualizar as integrações conectadas, mas não pode modificá-las.</AlertDescription>
            </Alert>
          )}

          <Card><CardContent className="p-6">
            {microvix_connected && !showForm ? (
              <div className="rounded-xl bg-muted p-4">
                <h2 className="mb-2 text-[13px] font-semibold text-foreground">Integração conectada</h2>
                <div className="space-y-1">
                  <p className="text-[13px] font-medium text-foreground">
                    Portal ID: {microvix_integration?.portal_id || 'N/A'}
                  </p>
                  <p className="text-[13px] text-muted-foreground">
                    Conectado em {microvix_integration?.connected_at ? new Date(microvix_integration.connected_at).toLocaleDateString('pt-BR') : 'N/A'}
                  </p>
                  <p className="text-[11.5px] text-faint-foreground">
                    Por: {microvix_integration?.connected_by || 'N/A'}
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-[13px] text-muted-foreground">
                  Conecte sua conta Microvix informando a API Key e o Portal ID.
                </p>

                {can_manage_resources ? (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid gap-2">
                      <Label htmlFor="api_key">API Key</Label>
                      <Input
                        id="api_key"
                        type="password"
                        value={form.data.api_key}
                        onChange={(e) => form.setData('api_key', e.target.value)}
                        placeholder="Informe a API Key do Linx Microvix"
                      />
                      {form.errors.api_key && <p className="text-sm text-destructive">{form.errors.api_key}</p>}
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="portal_id">Portal ID</Label>
                      <Input
                        id="portal_id"
                        value={form.data.portal_id}
                        onChange={(e) => form.setData('portal_id', e.target.value)}
                        placeholder="Informe o Portal ID"
                      />
                      {form.errors.portal_id && <p className="text-sm text-destructive">{form.errors.portal_id}</p>}
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" type="submit" disabled={form.processing}>
                        {form.processing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plug className="h-4 w-4" />}
                        {microvix_connected ? 'Reconectar' : 'Conectar'}
                      </Button>
                      {microvix_connected && showForm && (
                        <Button variant="outline" size="sm" onClick={() => setShowForm(false)}>
                          Cancelar
                        </Button>
                      )}
                    </div>
                  </form>
                ) : (
                  <p className="text-[13px] text-muted-foreground">
                    Entre em contato com o dono da organização para conectar o Linx Microvix.
                  </p>
                )}
              </div>
            )}
          </CardContent></Card>

          <AssignedWorkersBanner
            workers={digital_workers.filter((w) => w.assigned)}
            onAddWorker={() => setAssignWorkersOpen(true)}
            onRemoveWorker={(workerId) => router.delete('/dashboard/integrations/remove_worker', { data: { provider: 'microvix', worker_id: workerId }, preserveScroll: true })}
          />
        </div>
      </div>

      <AssignWorkersModal
        open={assignWorkersOpen}
        onClose={() => setAssignWorkersOpen(false)}
        providerLabel="Linx Microvix"
        workers={digital_workers}
        provider="microvix"
        saving={savingWorkerAssignment}
        onSavingChange={setSavingWorkerAssignment}
      />

      {justConnected.worker && (
        <PostConnectionModal
          open={justConnected.open}
          onClose={justConnected.close}
          worker={justConnected.worker}
          context="Linx Microvix"
        />
      )}
    </>
  );
};

MicrovixPage.layout = (page: React.ReactElement) => <DashboardLayout>{page}</DashboardLayout>;

export default MicrovixPage;
